import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { t, font, Avatar, Button } from "@realtyflows/ui";
import useResponsive from "../hooks/useResponsive";
import Alert from "../components/Alert";
import ConfirmModal from "../components/ConfirmModal";
import TrialGate from "../components/TrialGate";
import {
  getGmailStatus, getGmailAuthUrl, disconnectGmail,
  sendEmailApi, saveToken,
  getSentEmails, getContactsWithActivity,
  getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate,
  fixEmailTemplates,
} from "../api";

const AV_COLORS = [
  { bg: t.emeraldLight, fg: t.emeraldDeep },
  { bg: t.skyLight, fg: t.sky },
  { bg: t.amberLight, fg: t.emerald },
  { bg: t.purpleLight, fg: t.purple },
];

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}

function initials(name) {
  return (name || "?").split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase();
}

function formatDate(dateStr) {
  if (!dateStr) return "\u2014";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  if (diff < 172800000) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// SVG icon helper
const Icon = ({ children, size = 14, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} {...props}>{children}</svg>
);

// ============ Placeholder helpers ============

// Extract [bracket placeholder] names from text
function extractBracketFields(text) {
  const fields = new Set();
  const re = /\$?\[([^\]]+)\]/g;
  let m;
  while ((m = re.exec(text)) !== null) fields.add(m[1]);
  return [...fields];
}

// Auto-fill known {{template}} variables with client data
function autoFillVars(text, client) {
  const firstName = (client.name || "").split(" ")[0];
  return text
    .replace(/\{\{first_name\}\}/gi, firstName)
    .replace(/\{\{name\}\}/gi, client.name || "")
    .replace(/\{\{email\}\}/gi, client.email || "");
}

// Replace [field] or $[field] in text with value, or restore bracket if empty
function replaceField(text, key, oldVal, newVal) {
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (oldVal) {
    // Replace previous value (plain or with $ prefix)
    const escaped = oldVal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return text.replace(new RegExp(escaped, "g"), newVal || `[${key}]`);
  }
  // Replace bracket placeholder
  return text.replace(new RegExp(`\\$?\\[${escapedKey}\\]`, "g"), newVal || `[${key}]`);
}

// Check if text has any remaining [bracket] placeholders
function hasUnfilledBrackets(text) {
  return /\$?\[[^\]]+\]/.test(text);
}

// Render text with remaining [brackets] highlighted
function renderPreviewText(text) {
  const parts = text.split(/(\$?\[[^\]]+\])/g);
  return parts.map((part, i) =>
    /^\$?\[/.test(part)
      ? <span key={i} style={{ background: `${t.coral}20`, color: t.coral, padding: "1px 4px", borderRadius: 3, fontWeight: 500 }}>{part}</span>
      : part
  );
}

// ============ Email template chip system (visual chips in contentEditable) ============

const EMAIL_CHIP_STYLE = {
  auto: { bg: `${t.emerald}12`, bd: t.emerald, fg: t.emerald, bs: "solid" },
  fill: { bg: t.surfaceWarm || "#f5f5f0", bd: t.textMuted, fg: t.textSecondary, bs: "dashed" },
};

function buildEmailChip(type, raw, label) {
  const c = EMAIL_CHIP_STYLE[type];
  const safeRaw = raw.replace(/"/g, "&quot;");
  return (
    `<span contenteditable="false" data-field="${safeRaw}" data-type="${type}"` +
    ` style="display:inline-flex;align-items:center;gap:3px;` +
    `background:${c.bg};border:1.5px ${c.bs} ${c.bd};border-radius:5px;` +
    `padding:2px 8px 2px 10px;font-size:11px;color:${c.fg};font-weight:600;` +
    `white-space:nowrap;user-select:all;vertical-align:baseline;` +
    `font-family:sans-serif;cursor:default;margin:0 2px;line-height:20px;">` +
    `${label}<span data-remove="1" style="cursor:pointer;font-size:9px;` +
    `opacity:0.4;margin-left:3px;line-height:1;" title="Remove">\u2715</span></span>`
  );
}

const EMAIL_FIELDS = [
  { raw: "{{first_name}}", label: "First Name", type: "auto" },
  { raw: "{{name}}", label: "Full Name", type: "auto" },
  { raw: "{{email}}", label: "Email", type: "auto" },
  { raw: "[property address]", label: "Property Address", type: "fill" },
  { raw: "$[price]", label: "Price", type: "fill" },
  { raw: "[date]", label: "Date", type: "fill" },
  { raw: "[showing time]", label: "Showing Time", type: "fill" },
  { raw: "[listing agent name]", label: "Listing Agent", type: "fill" },
  { raw: "[lender name]", label: "Lender Name", type: "fill" },
  { raw: "[offer amount]", label: "Offer Amount", type: "fill" },
  { raw: "[neighborhood]", label: "Neighborhood", type: "fill" },
];

const CUSTOM_FIELD_TYPES = [
  { key: "text", label: "Text" },
  { key: "name", label: "Name" },
  { key: "address", label: "Address" },
  { key: "money", label: "Money" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "date", label: "Date" },
  { key: "number", label: "Number" },
  { key: "url", label: "URL" },
];

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function rawToEmailChips(text) {
  if (!text) return "";
  // Detect if body is already HTML (rich text) or plain text
  const isHtml = /<(b|i|u|s|strong|em|ul|ol|li|a |br|div|span|font|p[ >])[^>]*>/i.test(text);
  let out = isHtml ? text : escapeHtml(text).replace(/\n/g, "<br>");
  for (const f of EMAIL_FIELDS) {
    out = out.split(f.raw).join(buildEmailChip(f.type, f.raw, f.label));
    // Also match HTML-escaped version for backward compat with plain text
    if (!isHtml) {
      const escaped = escapeHtml(f.raw);
      if (escaped !== f.raw) out = out.split(escaped).join(buildEmailChip(f.type, f.raw, f.label));
    }
  }
  // Render any remaining [bracket] tokens as fill-type chips (custom fields)
  // Split on existing chip spans to avoid matching brackets inside HTML attributes
  const parts = out.split(/(<span[^>]*data-field[^>]*>.*?<\/span>)/g);
  out = parts.map(part => {
    if (part.startsWith("<span")) return part; // already a chip
    return part.replace(/\$?\[([^\]]+)\]/g, (match, inner) => buildEmailChip("fill", match, inner));
  }).join("");
  return out;
}

function chipsToEmailRaw(html) {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  // Replace chip spans with their raw token text
  el.querySelectorAll("[data-field]").forEach(n => {
    n.replaceWith(document.createTextNode(n.dataset.field));
  });
  // Remove orphaned remove buttons
  el.querySelectorAll("[data-remove]").forEach(n => n.remove());
  // Return HTML preserving formatting (bold, italic, links, lists, etc.)
  return el.innerHTML;
}

export default function Emails() {
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;

  const [gmail, setGmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("clients");
  const [showSidebar, setShowSidebar] = useState(true);

  // Data
  const [contacts, setContacts] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Filters
  const [clientSearch, setClientSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [sentSearch, setSentSearch] = useState("");
  const [sentFilter, setSentFilter] = useState("all");

  // Compose: { to, toName, contactId, templateId, subject, body, fields: {key: val}, step: "compose"|"preview" }
  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);
  const [trialGateCode, setTrialGateCode] = useState(null);
  const [trialGateMessage, setTrialGateMessage] = useState("");

  // Templates editing
  const [editTpl, setEditTpl] = useState(null); // { id, name, subject, body } or null
  const [previewTpl, setPreviewTpl] = useState(null); // template object for preview view
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Alert
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!alert) return;
    const timer = setTimeout(() => setAlert(null), 4000);
    return () => clearTimeout(timer);
  }, [alert]);

  // Handle OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("token")) {
      saveToken(params.get("token"));
      window.location.href = "/emails";
      return;
    }
    if (params.get("connected")) {
      window.history.replaceState({}, "", "/emails");
    }
    if (params.get("error")) {
      const errMsg = params.get("error") === "scope_denied"
        ? "Send permission was not granted. Please reconnect and make sure to check the permission checkbox."
        : "Gmail connection failed. Please try again.";
      setAlert({ type: "error", message: errMsg });
      window.history.replaceState({}, "", "/emails");
    }
  }, []);

  // Load Gmail status
  const loadStatus = useCallback(async () => {
    try {
      const status = await getGmailStatus();
      if (status.connected) {
        const hasSend = (status.scopes || []).some(s => s.includes("gmail.send"));
        if (!hasSend) {
          await disconnectGmail().catch(() => {});
          setGmail({ connected: false });
          setAlert({ type: "warning", message: "Gmail send permission was not granted. Please reconnect and check the permission box." });
        } else {
          setGmail(status);
        }
      } else {
        setGmail(status);
      }
    } catch { setGmail({ connected: false }); }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadData = useCallback(async () => {
    if (!gmail?.connected) return;
    try {
      // Fix any corrupted templates on first load, then fetch
      await fixEmailTemplates().catch(() => {});
      const [c, s, tpl] = await Promise.all([getContactsWithActivity(), getSentEmails(), getEmailTemplates()]);
      setContacts(c);
      setSentEmails(s);
      setTemplates(tpl);
    } catch (err) { console.error("Failed to load email data:", err); }
  }, [gmail?.connected]);

  useEffect(() => { loadData(); }, [loadData]);


  const connectGmail = async () => {
    try {
      const { url } = await getGmailAuthUrl(["send"]);
      window.location.href = url;
    } catch (err) { setAlert({ type: "error", message: err.message }); }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectGmail();
      setGmail({ connected: false });
      setAlert({ type: "success", message: "Gmail disconnected" });
    } catch (err) { setAlert({ type: "error", message: err.message }); }
  };

  // ============ Compose logic ============

  const openCompose = (client) => {
    if (isMobile) setShowSidebar(false);
    const firstName = client ? (client.name || "").split(" ")[0] : "";
    setCompose({
      to: client?.email || "",
      toName: client?.name || "",
      contactId: client?.id || null,
      templateId: null,
      subject: "",
      body: firstName ? `Hi ${firstName},\n\n` : "",
      fields: {},
      step: "compose",
    });
  };

  const applyTemplate = (tplId) => {
    setCompose(prev => {
      if (!prev) return prev;
      if (!tplId) {
        const firstName = (prev.toName || "").split(" ")[0];
        return { ...prev, templateId: null, templateSubject: null, templateBody: null, subject: "", body: firstName ? `Hi ${firstName},\n\n` : "", fields: {} };
      }
      const tpl = templates.find(t => t.id === parseInt(tplId));
      if (!tpl) return prev;

      const client = { name: prev.toName, email: prev.to };
      const subj = autoFillVars(tpl.subject || "", client);
      const body = autoFillVars(tpl.body || "", client);

      const fieldNames = extractBracketFields(subj + " " + body);
      const fields = {};
      fieldNames.forEach(f => { fields[f] = ""; });

      return { ...prev, templateId: tpl.id, templateSubject: subj, templateBody: body, subject: subj, body, fields, step: "compose" };
    });
  };

  // Apply all field values to template text (always starts from original template)
  const applyAllFields = (templateText, fields) => {
    let result = templateText;
    for (const [k, v] of Object.entries(fields)) {
      if (!v) continue;
      const escapedKey = k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      result = result.replace(new RegExp(`\\$?\\[${escapedKey}\\]`, "g"), v);
    }
    return result;
  };

  const updateField = (key, newValue) => {
    setCompose(prev => {
      const newFields = { ...prev.fields, [key]: newValue };
      // Recompute from original template text to avoid cascading replacements
      const baseSubject = prev.templateSubject || prev.subject;
      const baseBody = prev.templateBody || prev.body;
      return {
        ...prev,
        fields: newFields,
        subject: applyAllFields(baseSubject, newFields),
        body: applyAllFields(baseBody, newFields),
      };
    });
  };

  const handleSend = async () => {
    if (!compose?.to || !compose?.subject) {
      setAlert({ type: "error", message: "To and subject are required" });
      return;
    }
    // Require name for new contacts
    if (compose.to.includes("@") && !compose.contactId && !(compose.toName || "").trim()) {
      setAlert({ type: "error", message: "Please enter the recipient's name" });
      return;
    }
    // Warn about unfilled placeholders
    if (hasUnfilledBrackets(compose.subject + " " + compose.body)) {
      setAlert({ type: "warning", message: "Some fields are still unfilled. Please fill in all [bracket] fields or edit them out." });
      setCompose(prev => ({ ...prev, step: "compose" }));
      return;
    }
    setSending(true);
    try {
      await sendEmailApi({
        to: compose.to,
        subject: compose.subject,
        body: compose.body || "",
        contact_id: compose.contactId || null,
        template_id: compose.templateId || null,
        is_automated: false,
      });
      setCompose(null);
      setAlert({ type: "success", message: "Email sent" });
      const [s, c] = await Promise.all([getSentEmails(), getContactsWithActivity()]);
      setSentEmails(s);
      setContacts(c);
    } catch (err) {
      setAlert({ type: "error", message: err.message });
    }
    setSending(false);
  };

  // Template CRUD
  const handleSaveTemplate = async (tplData) => {
    const tpl = tplData || editTpl;
    if (!tpl?.name) { setAlert({ type: "error", message: "Template name is required" }); return; }
    try {
      if (tpl.id) {
        const updated = await updateEmailTemplate(tpl.id, tpl);
        setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      } else {
        const created = await createEmailTemplate(tpl);
        setTemplates(prev => [created, ...prev]);
      }
      setEditTpl(null);
      setAlert({ type: "success", message: tpl.id ? "Template updated" : "Template created" });
    } catch (err) { setAlert({ type: "error", message: err.message }); }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteEmailTemplate(deleteConfirm);
      setTemplates(prev => prev.filter(t => t.id !== deleteConfirm));
      setDeleteConfirm(null);
      setAlert({ type: "success", message: "Template deleted" });
    } catch (err) { setAlert({ type: "error", message: err.message }); }
  };

  // Filtered lists
  const filteredContacts = useMemo(() => {
    let list = contacts;
    if (clientSearch) {
      const q = clientSearch.toLowerCase();
      list = list.filter(c => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
    }
    if (clientFilter === "emailed") list = list.filter(c => c.sent_count > 0);
    if (clientFilter === "not-emailed") list = list.filter(c => !c.sent_count);
    return list;
  }, [contacts, clientSearch, clientFilter]);

  const filteredSent = useMemo(() => {
    let list = sentEmails;
    if (sentSearch) {
      const q = sentSearch.toLowerCase();
      list = list.filter(e => (e.contact_name || "").toLowerCase().includes(q) || (e.to_email || "").toLowerCase().includes(q) || (e.subject || "").toLowerCase().includes(q));
    }
    if (sentFilter === "automated") list = list.filter(e => e.is_automated);
    if (sentFilter === "manual") list = list.filter(e => !e.is_automated);
    return list;
  }, [sentEmails, sentSearch, sentFilter]);

  if (loading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.textMuted, fontFamily: font.body, fontSize: 14 }}>Loading...</div>;
  }

  // Connect screen
  if (!gmail?.connected) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontFamily: font.body, background: t.pageBg }}>
        {alert && <Alert variant={alert.type} message={alert.message} onDismiss={() => setAlert(null)} style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 200, minWidth: 300, boxShadow: `0 4px 16px ${t.shadowMd}` }} />}
        <div style={{ textAlign: "center", maxWidth: 420, padding: "40px 36px", background: t.surface, borderRadius: 12, border: `1px solid ${t.border}`, boxShadow: `0 4px 24px ${t.shadow}` }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: t.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon size={28} style={{ color: t.emerald }}><path d="M2 4h12v9H2z" /><path d="M2 4l6 5 6-5" /></Icon>
          </div>
          <div style={{ fontSize: 20, fontWeight: 600, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>Connect your Gmail</div>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 28, lineHeight: 1.7 }}>
            Send emails to your clients directly from RealtyFlows using your Gmail account. We only request <strong style={{ color: t.textPrimary }}>send permission</strong> — we never read your inbox.
          </div>
          <button onClick={connectGmail} style={{ background: t.navy, color: t.goldLight, border: "none", borderRadius: 8, padding: "12px 32px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: font.body, width: "100%", letterSpacing: 0.2 }}>Connect Gmail (Send Only)</button>
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { icon: <><path d="M2 4h12v9H2z" /><path d="M2 4l6 5 6-5" /></>, text: "Send emails from your Gmail address" },
              { icon: <><circle cx="8" cy="8" r="6" /><path d="M6 8l2 2 4-4" /></>, text: "Track all sent emails in one place" },
              { icon: <><rect x="2" y="2" width="12" height="12" rx="1" /><path d="M5 6h6M5 9h4" /></>, text: "Create reusable email templates" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: t.textMuted }}>
                <Icon size={13} style={{ color: t.emerald }}>{item.icon}</Icon>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const fieldKeys = compose ? Object.keys(compose.fields || {}) : [];
  const hasFields = fieldKeys.length > 0;
  const unfilledCount = fieldKeys.filter(k => !compose.fields[k]).length;

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "210px 1fr", height: "100%", background: t.surface, fontFamily: font.body, overflow: "hidden" }}>
      {alert && <Alert variant={alert.type} message={alert.message} onDismiss={() => setAlert(null)} style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 200, minWidth: 300, boxShadow: `0 4px 16px ${t.shadowMd}` }} />}

      {/* Sidebar */}
      {(!isMobile || showSidebar) && <div style={{ background: t.pageBg, borderRight: isMobile ? "none" : `0.5px solid ${t.border}`, padding: "14px 10px", display: "flex", flexDirection: "column", gap: 3 }}>
        <button onClick={() => openCompose(null)} style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          padding: 9, background: t.navy, color: t.goldLight, border: "none", borderRadius: 6,
          fontSize: 13, fontWeight: 500, cursor: "pointer", marginBottom: 6, fontFamily: font.body,
        }}>
          <Icon size={13} style={{ color: t.goldLight }}><path d="M2 11L1 15l4-1L13 6l-3-3-8 8z" /><path d="M10 3l3 3" /></Icon>
          Compose email
        </button>

        <span style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", padding: "8px 8px 3px", fontWeight: 500 }}>Views</span>

        {[
          { key: "clients", label: "Clients", count: contacts.length, icon: <><circle cx="6" cy="5" r="3" /><path d="M1 14s1-4 5-4 5 4 5 4" /><circle cx="12" cy="6" r="2" /><path d="M14 14s0-3-2-3" /></> },
          { key: "sent", label: "Sent emails", count: sentEmails.length, icon: <><path d="M2 4h12v9H2z" /><path d="M2 4l6 5 6-5" /></> },
          { key: "templates", label: "Templates", count: templates.length, icon: <><rect x="2" y="2" width="12" height="12" rx="1" /><path d="M5 6h6M5 9h4" /></> },
        ].map(item => (
          <div key={item.key} onClick={() => { setView(item.key); if (isMobile) setShowSidebar(false); if (item.key === "templates") { setEditTpl(null); setPreviewTpl(null); } }} style={{
            display: "flex", alignItems: "center", gap: 9, padding: "7px 10px", borderRadius: 6,
            fontSize: 13, cursor: "pointer",
            background: view === item.key ? t.emeraldLight : "transparent",
            color: view === item.key ? t.emeraldDeep : t.textMuted,
          }}>
            <Icon size={14}>{item.icon}</Icon>
            {item.label}
            <span style={{ marginLeft: "auto", background: view === item.key ? t.goldPale : t.surfaceWarm, color: view === item.key ? t.emeraldDeep : t.textMuted, fontSize: 11, padding: "1px 6px", borderRadius: 20 }}>{item.count}</span>
          </div>
        ))}

        <div style={{ marginTop: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 10px" }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.gold, flexShrink: 0, boxShadow: `0 0 6px ${t.aiGlow}` }} />
            <span style={{ fontSize: 11, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{gmail.email}</span>
          </div>
          <div style={{ fontSize: 10, color: t.textMuted, padding: "0 10px 4px", lineHeight: 1.5 }}>Send-only access.<br />Replies go to Gmail inbox.</div>
          <button onClick={handleDisconnect} style={{
            display: "block", width: "calc(100% - 20px)", margin: "6px 10px 8px",
            padding: "6px 0", fontSize: 11, fontWeight: 500, color: t.coral,
            background: t.coralLight, border: `1px solid ${t.coral}22`, borderRadius: 6,
            cursor: "pointer", textAlign: "center", fontFamily: font.body,
          }}>Disconnect Gmail</button>
        </div>
      </div>}

      {/* Main */}
      {(!isMobile || !showSidebar) && <div style={{ display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
        {/* Mobile back button */}
        {isMobile && (
          <button onClick={() => setShowSidebar(true)} style={{
            display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            cursor: "pointer", color: t.textSecondary, fontSize: 13, fontFamily: font.body,
            padding: "10px 16px", borderBottom: `0.5px solid ${t.border}`,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Menu
          </button>
        )}
        {view === "clients" && <ClientsView contacts={filteredContacts} totalCount={contacts.length} search={clientSearch} onSearch={setClientSearch} filter={clientFilter} onFilter={setClientFilter} onEmail={openCompose} isMobile={isMobile} />}
        {view === "sent" && <SentView emails={filteredSent} totalCount={sentEmails.length} search={sentSearch} onSearch={setSentSearch} filter={sentFilter} onFilter={setSentFilter} isMobile={isMobile} />}
        {view === "templates" && !editTpl && !previewTpl && <TemplatesView templates={templates}
          onPreview={setPreviewTpl}
          onEdit={(tpl) => { setPreviewTpl(null); setEditTpl(tpl); }}
          onDelete={setDeleteConfirm}
          onReorder={setTemplates}
          onUse={(tpl) => { openCompose(null); setTimeout(() => applyTemplate(String(tpl.id)), 0); }}
        />}
        {view === "templates" && previewTpl && !editTpl && <TemplatePreviewPanel
          template={previewTpl}
          onEdit={() => { setEditTpl({ id: previewTpl.id, name: previewTpl.name, subject: previewTpl.subject, body: previewTpl.body }); setPreviewTpl(null); }}
          onUse={() => { openCompose(null); setTimeout(() => applyTemplate(String(previewTpl.id)), 0); setPreviewTpl(null); }}
          onBack={() => setPreviewTpl(null)}
          isMobile={isMobile}
        />}
        {view === "templates" && editTpl && <TemplateEditPage
          template={editTpl}
          onChange={setEditTpl}
          onSave={handleSaveTemplate}
          onBack={() => { setEditTpl(null); }}
          isMobile={isMobile}
        />}
      </div>}

      {/* ============ Compose Modal ============ */}
      {compose && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setCompose(null); }} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.28)",
          display: "flex", alignItems: isMobile ? "stretch" : "flex-end", justifyContent: isMobile ? "stretch" : "flex-end",
          padding: isMobile ? 0 : 20, zIndex: 100,
        }}>
          <div style={{ width: isMobile ? "100%" : 520, maxHeight: isMobile ? "100%" : "85vh", height: isMobile ? "100%" : "auto", display: "flex", flexDirection: "column", background: t.surface, border: isMobile ? "none" : `0.5px solid ${t.border}`, borderRadius: isMobile ? 0 : 10, overflow: "hidden", boxShadow: isMobile ? "none" : `0 8px 32px ${t.shadowMd}` }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", background: t.navy, color: t.goldLight, flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                {compose.step === "preview" ? "Preview email" : compose.toName ? `Email ${compose.toName}` : "New email"}
              </span>
              <button onClick={() => setCompose(null)} style={{ background: "none", border: "none", color: t.goldLight, cursor: "pointer", fontSize: 17, opacity: 0.7 }}>&times;</button>
            </div>

            {compose.step === "preview" ? (
              /* ============ PREVIEW MODE ============ */
              <>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {/* To / Subject */}
                  <div style={{ padding: "10px 16px", borderBottom: `0.5px solid ${t.border}`, background: t.pageBg }}>
                    <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>To: <span style={{ color: t.textPrimary }}>{compose.to}</span></div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>{renderPreviewText(compose.subject)}</div>
                  </div>

                  {/* Unfilled warning */}
                  {hasUnfilledBrackets(compose.subject + " " + compose.body) && (
                    <div style={{ padding: "8px 16px", background: `${t.coral}10`, borderBottom: `1px solid ${t.coral}20`, fontSize: 12, color: t.coral, display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 14 }}>&#9888;</span>
                      Some fields are still unfilled. Go back to fill them in before sending.
                    </div>
                  )}

                  {/* Body preview */}
                  <div style={{ padding: "16px", fontSize: 13, lineHeight: 1.7, color: t.textPrimary, whiteSpace: "pre-line" }}
                    dangerouslySetInnerHTML={{ __html: (compose.body || "").replace(/\$?\[([^\]]+)\]/g, '<span style="background:#f4433620;color:#f44336;padding:1px 4px;border-radius:3px;font-weight:500">[$1]</span>') }}
                  />
                </div>

                {/* Preview footer */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: `0.5px solid ${t.border}`, flexShrink: 0 }}>
                  <button onClick={() => setCompose(prev => ({ ...prev, step: "compose" }))} style={{
                    display: "flex", alignItems: "center", gap: 5,
                    fontSize: 12, padding: "7px 14px", background: t.surface, color: t.textSecondary,
                    border: `0.5px solid ${t.border}`, borderRadius: 6, cursor: "pointer", fontFamily: font.body,
                  }}>
                    <Icon size={12}><polyline points="9 12 4 8 9 4" /><path d="M4 8h10" /></Icon>
                    Back to edit
                  </button>
                  <button onClick={handleSend} disabled={sending || hasUnfilledBrackets(compose.subject + " " + compose.body)} style={{
                    background: t.navy, color: t.goldLight, border: "none", padding: "7px 20px",
                    borderRadius: 6, fontSize: 13, fontWeight: 500, fontFamily: font.body,
                    cursor: (sending || hasUnfilledBrackets(compose.subject + " " + compose.body)) ? "not-allowed" : "pointer",
                    opacity: (sending || hasUnfilledBrackets(compose.subject + " " + compose.body)) ? 0.5 : 1,
                  }}>{sending ? "Sending..." : "Send email"}</button>
                </div>
              </>
            ) : (
              /* ============ COMPOSE MODE ============ */
              <>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {/* From */}
                  <div style={{ borderBottom: `0.5px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8, padding: "7px 16px" }}>
                    <label style={{ fontSize: 11, color: t.textMuted, minWidth: 60 }}>From</label>
                    <span style={{ fontSize: 13, color: t.textSecondary }}>{gmail.email}</span>
                  </div>
                  {/* To — Client autocomplete */}
                  <div style={{ borderBottom: `0.5px solid ${t.border}`, position: "relative" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px" }}>
                      <label style={{ fontSize: 11, color: t.textMuted, minWidth: 60 }}>To</label>
                      <input value={compose.to}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Auto-match against existing clients
                          const match = contacts.find(c => c.email && c.email.toLowerCase() === val.toLowerCase());
                          if (match) {
                            setCompose(prev => ({ ...prev, to: val, toName: match.name || "", contactId: match.id }));
                          } else {
                            setCompose(prev => ({ ...prev, to: val, toName: "", contactId: null }));
                          }
                        }}
                        onFocus={() => setCompose(prev => ({ ...prev, showToDropdown: true }))}
                        onBlur={() => setTimeout(() => setCompose(prev => prev ? { ...prev, showToDropdown: false } : null), 150)}
                        placeholder="Type client name or email..."
                        style={{ flex: 1, border: "none", outline: "none", fontSize: 13, background: "transparent", color: t.textPrimary, fontFamily: font.body }}
                      />
                    </div>
                    {/* Client dropdown */}
                    {compose.showToDropdown && (() => {
                      const q = (compose.to || "").toLowerCase();
                      const matches = contacts.filter(c =>
                        c.email && ((c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q))
                      ).slice(0, 6);
                      if (matches.length === 0) return null;
                      return (
                        <div style={{
                          position: "absolute", left: 16, right: 16, top: "100%", zIndex: 100,
                          background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxHeight: 200, overflowY: "auto",
                        }}>
                          {matches.map(c => {
                            const ac = avatarColor(c.name);
                            return (
                              <div key={c.id} onMouseDown={() => {
                                setCompose(prev => {
                                  const updated = { ...prev, to: c.email, toName: c.name || "", contactId: c.id, showToDropdown: false };
                                  // Re-apply template vars with new client data AND preserve filled fields
                                  if (prev.templateId) {
                                    const tpl = templates.find(tp => tp.id === parseInt(prev.templateId));
                                    if (tpl) {
                                      const client = { name: c.name || "", email: c.email };
                                      const newTemplateSubject = autoFillVars(tpl.subject || "", client);
                                      const newTemplateBody = autoFillVars(tpl.body || "", client);
                                      updated.templateSubject = newTemplateSubject;
                                      updated.templateBody = newTemplateBody;
                                      updated.subject = applyAllFields(newTemplateSubject, prev.fields || {});
                                      updated.body = applyAllFields(newTemplateBody, prev.fields || {});
                                    }
                                  }
                                  return updated;
                                });
                              }} style={{
                                display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                                cursor: "pointer", transition: "background 0.1s",
                              }}
                                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <div style={{
                                  width: 28, height: 28, borderRadius: "50%", background: ac.bg,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 10, fontWeight: 700, color: ac.fg, flexShrink: 0,
                                }}>{initials(c.name)}</div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{c.name || "Unknown"}</div>
                                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{c.email}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Name input for new contacts */}
                  {compose.to && compose.to.includes("@") && !compose.contactId && (
                    <div style={{ borderBottom: `0.5px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", background: `${t.emerald}06` }}>
                      <label style={{ fontSize: 11, color: t.emerald, minWidth: 60, fontWeight: 600 }}>Name *</label>
                      <input value={compose.toName || ""}
                        onChange={(e) => setCompose(prev => ({ ...prev, toName: e.target.value }))}
                        placeholder="Recipient's full name (required)"
                        style={{ flex: 1, border: "none", outline: "none", fontSize: 13, background: "transparent", color: t.textPrimary, fontFamily: font.body }}
                      />
                    </div>
                  )}
                  {/* Template */}
                  <div style={{ borderBottom: `0.5px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8, padding: "7px 16px" }}>
                    <label style={{ fontSize: 11, color: t.textMuted, minWidth: 60 }}>Template</label>
                    <select value={compose.templateId || ""} onChange={(e) => applyTemplate(e.target.value)}
                      style={{ flex: 1, border: "none", outline: "none", fontSize: 13, background: "transparent", color: t.textPrimary, fontFamily: font.body }}>
                      <option value="">None — write manually</option>
                      {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                    </select>
                  </div>

                  {/* Fill-in fields */}
                  {hasFields && (
                    <div style={{ padding: "10px 16px", borderBottom: `0.5px solid ${t.border}`, background: t.pageBg }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <Icon size={12} style={{ color: t.emerald }}><circle cx="8" cy="8" r="6" /><path d="M8 5v3M8 10v1" /></Icon>
                        <span style={{ fontSize: 11, fontWeight: 600, color: t.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>Fill in details</span>
                        {unfilledCount > 0 && <span style={{ fontSize: 10, color: t.coral, marginLeft: 4 }}>{unfilledCount} remaining</span>}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: fieldKeys.length > 2 ? "1fr 1fr" : "1fr", gap: 6 }}>
                        {fieldKeys.map(key => (
                          <div key={key}>
                            <label style={{ fontSize: 10, color: t.textMuted, textTransform: "capitalize", display: "block", marginBottom: 2 }}>{key}</label>
                            <input
                              value={compose.fields[key]}
                              onChange={(e) => updateField(key, e.target.value)}
                              placeholder={`Enter ${key.toLowerCase()}...`}
                              style={{
                                width: "100%", padding: "5px 8px", fontSize: 12,
                                border: `1px solid ${compose.fields[key] ? t.border : `${t.coral}60`}`,
                                borderRadius: 5, fontFamily: font.body, background: t.surface,
                                color: t.textPrimary, outline: "none", boxSizing: "border-box",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subject */}
                  <div style={{ borderBottom: `0.5px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8, padding: "7px 16px" }}>
                    <label style={{ fontSize: 11, color: t.textMuted, minWidth: 60 }}>Subject</label>
                    <input value={compose.subject} onChange={(e) => setCompose(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Subject line" style={{ flex: 1, border: "none", outline: "none", fontSize: 13, background: "transparent", color: t.textPrimary, fontFamily: font.body }} />
                  </div>
                  {/* Body */}
                  <textarea
                    value={compose.body}
                    onChange={(e) => setCompose(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Write your message..."
                    rows={8}
                    style={{ width: "100%", border: "none", outline: "none", fontSize: 13, resize: "none", background: "transparent", color: t.textPrimary, minHeight: 140, lineHeight: 1.7, padding: "10px 16px", fontFamily: font.body, boxSizing: "border-box" }}
                  />
                </div>

                {/* Compose footer */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: `0.5px solid ${t.border}`, flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: t.textMuted }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.gold, boxShadow: `0 0 6px ${t.aiGlow}` }} />
                    Sending via Gmail
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setCompose(prev => ({ ...prev, step: "preview" }))} disabled={!compose.subject}
                      style={{
                        fontSize: 12, padding: "7px 14px", background: t.surface, color: t.textSecondary,
                        border: `0.5px solid ${t.border}`, borderRadius: 6, cursor: compose.subject ? "pointer" : "not-allowed",
                        fontFamily: font.body, opacity: compose.subject ? 1 : 0.5,
                      }}>Preview</button>
                    <button onClick={handleSend} disabled={sending} style={{
                      background: t.navy, color: t.goldLight, border: "none", padding: "7px 18px",
                      borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: sending ? "not-allowed" : "pointer",
                      opacity: sending ? 0.6 : 1, fontFamily: font.body,
                    }}>{sending ? "Sending..." : "Send"}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmModal title="Delete Template" message="Are you sure you want to delete this email template?" confirmLabel="Delete"
          onCancel={() => setDeleteConfirm(null)} onConfirm={handleDeleteTemplate} />
      )}

      {trialGateCode && (
        <TrialGate code={trialGateCode} message={trialGateMessage} onDismiss={() => { setTrialGateCode(null); setTrialGateMessage(""); }} />
      )}
    </div>
  );
}

// ============ Clients View ============
function ClientsView({ contacts, totalCount, search, onSearch, filter, onFilter, onEmail, isMobile }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `0.5px solid ${t.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: t.textPrimary, fontFamily: font.display }}>Clients</span>
          <span style={{ fontSize: 11, color: t.textMuted, background: t.surfaceWarm, padding: "2px 8px", borderRadius: 20 }}>{contacts.length} clients</span>
        </div>
      </div>
      <SearchBar search={search} onSearch={onSearch} placeholder="Search by name or email..." tabs={[
        { key: "all", label: "All" }, { key: "emailed", label: "Emailed" }, { key: "not-emailed", label: "Not emailed" },
      ]} active={filter} onTab={onFilter} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr auto" : "2fr 1.6fr 90px 70px 80px", gap: 10, padding: "7px 18px", borderBottom: `0.5px solid ${t.border}`, background: t.pageBg }}>
        {(isMobile ? ["Client", "Date"] : ["Client", "Last email sent", "Date", "Sent", ""]).map((h, i) => (
          <div key={i} style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</div>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {contacts.length === 0 && (
          <div style={{ padding: "40px 18px", textAlign: "center", color: t.textMuted, fontSize: 13 }}>
            {totalCount === 0 ? "No clients yet. Add clients to start sending emails." : "No clients match your search."}
          </div>
        )}
        {contacts.map(c => {
          const av = avatarColor(c.name);
          return (
            <div key={c.id} onClick={() => { if (isMobile) onEmail(c); }} style={{
              display: "grid", gridTemplateColumns: isMobile ? "1fr auto" : "2fr 1.6fr 90px 70px 80px", gap: 10,
              alignItems: "center", padding: "9px 18px", borderBottom: `0.5px solid ${t.border}`, cursor: "pointer",
            }} onMouseEnter={e => e.currentTarget.style.background = t.pageBg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 500, flexShrink: 0, background: av.bg, color: av.fg }}>
                  {initials(c.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: t.textPrimary }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.email}</div>
                </div>
              </div>
              {!isMobile && <div style={{ fontSize: 12, color: t.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.last_subject || "\u2014"}</div>}
              <div style={{ fontSize: 12, color: t.textMuted }}>{formatDate(c.last_email_sent)}</div>
              {!isMobile && <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary, fontFamily: font.mono }}>{c.sent_count || "\u2014"}</div>}
              {!isMobile && <div>
                <button onClick={(e) => { e.stopPropagation(); onEmail(c); }} style={{
                  display: "flex", alignItems: "center", gap: 5,
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
                  padding: "5px 12px", border: "none", borderRadius: 4,
                  cursor: "pointer", background: t.navyLight, color: t.goldLight,
                  whiteSpace: "nowrap", fontFamily: font.body,
                }}>
                  <Icon size={10} style={{ color: t.goldLight }}><path d="M2 4h12v9H2z" /><path d="M2 4l6 5 6-5" /></Icon>
                  Email
                </button>
              </div>}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ============ Sent View (expandable detail on click) ============
function SentView({ emails, totalCount, search, onSearch, filter, onFilter, isMobile }) {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `0.5px solid ${t.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: t.textPrimary, fontFamily: font.display }}>Sent emails</span>
          <span style={{ fontSize: 11, color: t.textMuted, background: t.surfaceWarm, padding: "2px 8px", borderRadius: 20 }}>{emails.length} sent</span>
        </div>
      </div>
      <SearchBar search={search} onSearch={onSearch} placeholder="Search sent emails..." tabs={[
        { key: "all", label: "All" }, { key: "automated", label: "Automated" }, { key: "manual", label: "Manual" },
      ]} active={filter} onTab={onFilter} />
      <div style={{ padding: "10px 18px", background: t.emeraldLight, borderBottom: `0.5px solid ${t.borderGold}`, fontSize: 12, color: t.emeraldDeep, display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.gold, flexShrink: 0 }} />
        Emails sent from RealtyFlows via your Gmail account. Replies go directly to your Gmail inbox.
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {emails.length === 0 && (
          <div style={{ padding: "40px 18px", textAlign: "center", color: t.textMuted, fontSize: 13 }}>
            {totalCount === 0 ? "No emails sent yet." : "No emails match your filter."}
          </div>
        )}
        {emails.map(e => {
          const name = e.contact_name || e.to_email;
          const av = avatarColor(name);
          const expanded = expandedId === e.id;
          return (
            <div key={e.id} style={{ borderBottom: `0.5px solid ${t.border}`, overflow: "hidden" }}>
              {/* Row */}
              <div onClick={() => setExpandedId(expanded ? null : e.id)} style={{
                display: "grid", gridTemplateColumns: "30px 1fr auto 18px", gap: 10,
                alignItems: "center", padding: "10px 18px", cursor: "pointer",
                background: expanded ? t.pageBg : "transparent",
              }} onMouseEnter={ev => { if (!expanded) ev.currentTarget.style.background = t.pageBg; }} onMouseLeave={ev => { if (!expanded) ev.currentTarget.style.background = "transparent"; }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 500, background: av.bg, color: av.fg }}>
                  {initials(name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary }}>{name}</div>
                  <div style={{ fontSize: 12, color: t.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.subject}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: t.textMuted }}>{formatDate(e.sent_at)}</span>
                  <span style={{
                    fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 500,
                    background: e.is_automated ? t.emeraldLight : t.skyLight,
                    color: e.is_automated ? t.emeraldDeep : t.sky,
                  }}>{e.is_automated ? "Automated" : "Manual"}</span>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0)" }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {/* Expanded Detail */}
              {expanded && (
                <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${t.border}`, background: t.pageBg }}>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 200px", gap: 20, paddingTop: 14 }}>
                    {/* Email content */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, letterSpacing: 0.5, marginBottom: 6 }}>EMAIL CONTENT</div>
                      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, overflow: "hidden" }}>
                        {/* Subject header */}
                        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}` }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>{e.subject || "No subject"}</div>
                          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
                            To: <span style={{ color: t.textSecondary }}>{e.to_email}</span>
                            {e.cc && <> &middot; Cc: <span style={{ color: t.textSecondary }}>{e.cc}</span></>}
                          </div>
                        </div>
                        {/* Body */}
                        <div style={{ padding: "14px 16px", fontSize: 13, lineHeight: 1.7, color: t.textPrimary, fontFamily: font.body, whiteSpace: "pre-line", maxHeight: 300, overflowY: "auto" }}>
                          {e.body || <span style={{ color: t.textMuted, fontStyle: "italic" }}>No body content</span>}
                        </div>
                      </div>
                    </div>

                    {/* Details sidebar */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, letterSpacing: 0.5, marginBottom: 6 }}>DETAILS</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12 }}>
                          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 2 }}>SENT</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                            {e.sent_at ? new Date(e.sent_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "\u2014"}
                          </div>
                        </div>
                        <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12 }}>
                          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 2 }}>TYPE</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: e.is_automated ? t.emerald : t.sky, fontFamily: font.body }}>
                            {e.is_automated ? "Automated" : "Manual"}
                          </div>
                        </div>
                        {e.template_name && (
                          <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12 }}>
                            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 2 }}>TEMPLATE</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{e.template_name}</div>
                          </div>
                        )}
                        {e.contact_name && (
                          <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12 }}>
                            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 2 }}>CLIENT</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{e.contact_name}</div>
                          </div>
                        )}
                        {e.gmail_message_id && (
                          <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12 }}>
                            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 2 }}>GMAIL ID</div>
                            <div style={{ fontSize: 10, color: t.textSecondary, fontFamily: font.mono, wordBreak: "break-all" }}>{e.gmail_message_id}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ============ Templates View (card-based, draggable — matches contract templates) ============
function TemplatesView({ templates, onPreview, onEdit, onDelete, onReorder, onUse }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [menuId, setMenuId] = useState(null);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>Email Templates</span>
          <span style={{ fontSize: 11, color: t.textMuted, background: t.surfaceWarm, padding: "2px 8px", borderRadius: 20 }}>{templates.length}</span>
        </div>
        <Button variant="primary" size="sm" onClick={() => onEdit({ name: "", subject: "", body: "" })}>
          + New Template
        </Button>
      </div>

      {/* Template cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {templates.map((tpl, idx) => (
          <div key={tpl.id}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
            onDragEnd={() => {
              if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
                const reordered = [...templates];
                const [moved] = reordered.splice(dragIdx, 1);
                reordered.splice(dragOverIdx, 0, moved);
                onReorder(reordered);
              }
              setDragIdx(null);
              setDragOverIdx(null);
            }}
            style={{
              background: t.surface,
              border: `1px solid ${dragOverIdx === idx ? t.emerald : t.border}`,
              borderRadius: 10,
              padding: "16px 20px", display: "flex", alignItems: "center", gap: 16,
              transition: "border-color 0.15s",
              opacity: dragIdx === idx ? 0.5 : 1,
              cursor: "grab",
            }}
            onMouseEnter={(e) => { if (dragIdx === null) e.currentTarget.style.borderColor = t.emerald + "50"; }}
            onMouseLeave={(e) => { if (dragIdx === null) e.currentTarget.style.borderColor = t.border; }}
          >
            {/* Drag handle */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2, cursor: "grab", flexShrink: 0, padding: "4px 0" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill={t.textMuted}>
                <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
                <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
              </svg>
            </div>

            {/* Clickable: Icon + Info → opens preview */}
            <div onClick={() => onPreview(tpl)} style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0, cursor: "pointer" }} draggable={false}>
              <div style={{
                width: 40, height: 40, borderRadius: 8, background: `${t.emerald}12`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Icon size={20} style={{ color: t.emerald }}><rect x="2" y="2" width="12" height="12" rx="1" /><path d="M5 6h6M5 9h4" /></Icon>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                  {tpl.name}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tpl.subject || "No subject"}
                </div>
              </div>
            </div>

            {/* Action Buttons — same as contract templates */}
            <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onPreview(tpl); }}>
                Preview
              </Button>
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onEdit({ id: tpl.id, name: tpl.name, subject: tpl.subject, body: tpl.body }); }}>
                Edit
              </Button>
              <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); onUse(tpl); }}>
                Use
              </Button>
              {/* Three-dot menu */}
              <div style={{ position: "relative" }}>
                <div onClick={(e) => { e.stopPropagation(); setMenuId(menuId === tpl.id ? null : tpl.id); }}
                  style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, cursor: "pointer", background: menuId === tpl.id ? t.surfaceWarm : "transparent", transition: "background 0.15s" }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill={t.textMuted}><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                </div>
                {menuId === tpl.id && (
                  <>
                    <div onClick={() => setMenuId(null)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} />
                    <div style={{ position: "absolute", right: 0, top: 32, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 100, minWidth: 160, overflow: "hidden" }}>
                      <div onClick={(e) => { e.stopPropagation(); setMenuId(null); onDelete(tpl.id); }}
                        style={{ padding: "10px 14px", fontSize: 12, color: t.coral, cursor: "pointer", fontFamily: font.body, display: "flex", alignItems: "center", gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.coral} strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                        Delete Template
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div style={{
            padding: 60, textAlign: "center", background: t.surface,
            border: `1px solid ${t.border}`, borderRadius: 12,
          }}>
            <div style={{ marginBottom: 12 }}>
              <Icon size={48} style={{ color: t.textMuted, opacity: 0.4 }}><rect x="2" y="2" width="12" height="12" rx="1" /><path d="M5 6h6M5 9h4" /></Icon>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, marginBottom: 4 }}>
              No templates yet
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>
              Create a template to speed up your emails
            </div>
            <Button variant="primary" size="sm" onClick={() => onEdit({ name: "", subject: "", body: "" })}>
              Create Template
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Template Preview Panel (matches contract TemplatePreview) ============
function TemplatePreviewPanel({ template, onEdit, onUse, onBack, isMobile }) {
  // Build preview with sample data
  const previewSubject = (template.subject || "")
    .replace(/\{\{first_name\}\}/gi, "John")
    .replace(/\{\{name\}\}/gi, "John Smith")
    .replace(/\{\{email\}\}/gi, "john@example.com");

  const previewBody = (template.body || "")
    .replace(/\{\{first_name\}\}/gi, "John")
    .replace(/\{\{name\}\}/gi, "John Smith")
    .replace(/\{\{email\}\}/gi, "john@example.com");

  // Extract variables used
  const autoVarsUsed = [];
  if (/\{\{first_name\}\}/i.test(template.subject + template.body)) autoVarsUsed.push("First Name");
  if (/\{\{name\}\}/i.test(template.subject + template.body)) autoVarsUsed.push("Full Name");
  if (/\{\{email\}\}/i.test(template.subject + template.body)) autoVarsUsed.push("Email");
  const bracketFields = extractBracketFields((template.subject || "") + " " + (template.body || ""));

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 12 : 24, animation: "fadeSlide 0.3s ease" }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile ? 16 : 24 }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
          cursor: "pointer", color: t.textSecondary, fontSize: 13, fontFamily: font.body, padding: 0,
        }}
          onMouseEnter={(e) => (e.currentTarget.style.color = t.emerald)}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textSecondary)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          Back to Templates
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
          <Button variant="primary" size="sm" onClick={onUse}>Use Template</Button>
        </div>
      </div>

      {/* Template Card */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden", maxWidth: 800, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 10, background: `${t.emerald}12`,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Icon size={24} style={{ color: t.emerald }}><rect x="2" y="2" width="12" height="12" rx="1" /><path d="M5 6h6M5 9h4" /></Icon>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
                {template.name}
              </div>
              <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, marginTop: 2 }}>
                {template.subject || "No subject"}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {autoVarsUsed.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: `${t.emerald}15`, color: t.emerald, fontFamily: font.mono }}>
                    {autoVarsUsed.length} AUTO-FILL
                  </span>
                )}
                {bracketFields.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: `${t.amber}15`, color: t.amber, fontFamily: font.mono }}>
                    {bracketFields.length} FILL-IN
                  </span>
                )}
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: t.surfaceWarm, color: t.textMuted, fontFamily: font.mono }}>
                  EMAIL TEMPLATE
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Email Preview */}
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, letterSpacing: 1 }}>EMAIL PREVIEW</div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>Variables shown with sample data</div>
          </div>
          <div style={{
            border: `1.5px solid ${t.border}`, borderRadius: 10, overflow: "hidden", background: "#fff",
          }}>
            {/* Subject bar */}
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border}`, background: t.pageBg }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>SUBJECT</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>
                {renderPreviewText(previewSubject) || <span style={{ color: t.textMuted }}>No subject</span>}
              </div>
            </div>
            {/* Body */}
            {previewBody ? (
              <div style={{ padding: "20px", fontSize: 13, lineHeight: 1.7, color: t.textPrimary, fontFamily: font.body, whiteSpace: "pre-line", minHeight: 200 }}
                dangerouslySetInnerHTML={{ __html: previewBody.replace(/\$?\[([^\]]+)\]/g, '<span style="background:#f4433620;color:#f44336;padding:1px 4px;border-radius:3px;font-weight:500">[$1]</span>') }}
              />
            ) : (
              <div style={{ padding: "20px", fontSize: 13, lineHeight: 1.7, color: t.textMuted, fontStyle: "italic", minHeight: 200 }}>No body content</div>
            )}
            {/* Legend */}
            <div style={{
              padding: "12px 20px", background: t.surfaceWarm, borderTop: `1px solid ${t.border}`,
              display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 3, background: `${t.emerald}15`, border: `1.5px solid ${t.emerald}` }} />
                <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body }}>Auto-filled from Client</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 3, background: `${t.coral}20`, border: `1.5px solid ${t.coral}` }} />
                <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body }}>Agent Fills In</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div style={{ padding: "24px 28px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 12, fontFamily: font.mono, letterSpacing: 1 }}>DETAILS</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14 }}>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>AUTO-FILL VARIABLES</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.emerald, fontFamily: font.mono }}>{autoVarsUsed.length}</div>
              {autoVarsUsed.length > 0 && (
                <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>{autoVarsUsed.join(", ")}</div>
              )}
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>FILL-IN FIELDS</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.amber, fontFamily: font.mono }}>{bracketFields.length}</div>
              {bracketFields.length > 0 && (
                <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>{bracketFields.join(", ")}</div>
              )}
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>CREATED</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                {template.created_at ? new Date(template.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "\u2014"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Template Edit Page (contentEditable with visual chips) ============
function TemplateEditPage({ template, onChange, onSave, onBack, isMobile }) {
  const [saving, setSaving] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customType, setCustomType] = useState("text");
  const lastFocusRef = useRef("body");
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const fieldsRef = useRef(null);
  const subjectCursorRef = useRef(null);
  const bodyCursorRef = useRef(null);

  // Initialize contentEditable HTML once imperatively (so React never resets it)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (subjectRef.current && !subjectRef.current.dataset.init) {
      subjectRef.current.innerHTML = rawToEmailChips(template.subject || "");
      subjectRef.current.dataset.init = "1";
    }
    if (bodyRef.current && !bodyRef.current.dataset.init) {
      bodyRef.current.innerHTML = rawToEmailChips(template.body || "");
      bodyRef.current.dataset.init = "1";
    }
  }, []);

  // Save cursor on every selection change (works even when sidebar chip clicked)
  const saveCursor = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (subjectRef.current?.contains(range.commonAncestorContainer)) {
      subjectCursorRef.current = range.cloneRange();
      lastFocusRef.current = "subject";
    } else if (bodyRef.current?.contains(range.commonAncestorContainer)) {
      bodyCursorRef.current = range.cloneRange();
      lastFocusRef.current = "body";
    }
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", saveCursor);
    return () => document.removeEventListener("selectionchange", saveCursor);
  }, [saveCursor]);

  const restoreCursor = (editorEl, cursorVal) => {
    const sel = window.getSelection();
    if (cursorVal) {
      try { sel.removeAllRanges(); sel.addRange(cursorVal); return; } catch { /* stale range */ }
    }
    if (editorEl) {
      const range = document.createRange();
      range.selectNodeContents(editorEl);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const syncToParent = () => {
    const subj = subjectRef.current ? chipsToEmailRaw(subjectRef.current.innerHTML) : (template.subject || "");
    const body = bodyRef.current ? chipsToEmailRaw(bodyRef.current.innerHTML) : (template.body || "");
    onChange({ ...template, subject: subj, body });
  };

  const insertChip = (type, raw, label) => {
    const isSubject = lastFocusRef.current === "subject";
    const editorEl = isSubject ? subjectRef.current : bodyRef.current;
    const cursorVal = isSubject ? subjectCursorRef.current : bodyCursorRef.current;
    if (!editorEl) return;
    editorEl.focus();
    restoreCursor(editorEl, cursorVal);
    // Ensure selection is inside the editor
    const sel = window.getSelection();
    if (!sel.rangeCount || !editorEl.contains(sel.anchorNode)) {
      const r = document.createRange();
      r.selectNodeContents(editorEl);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    const chipHtml = buildEmailChip(type, raw, label) + "\u00A0";
    if (!document.execCommand("insertHTML", false, chipHtml)) {
      // Fallback: DOM insertion
      const temp = document.createElement("span");
      temp.innerHTML = chipHtml;
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const frag = document.createDocumentFragment();
      while (temp.firstChild) frag.appendChild(temp.firstChild);
      range.insertNode(frag);
      range.collapse(false);
    }
    saveCursor();
    syncToParent();
  };

  const handleAddCustomField = () => {
    if (!customName.trim()) return;
    const name = customName.trim();
    const raw = customType === "money" ? `$[${name}]` : `[${name}]`;
    insertChip("fill", raw, name);
    setCustomName("");
  };

  const handleEditorClick = (e) => {
    const removeBtn = e.target.closest ? e.target.closest("[data-remove]") : (e.target.dataset?.remove ? e.target : null);
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const chip = removeBtn.closest ? removeBtn.closest("[data-field]") : removeBtn.parentElement;
      if (chip) { chip.remove(); syncToParent(); }
      return;
    }
    saveCursor();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const handleSave = async () => {
    setSaving(true);
    const subj = subjectRef.current ? chipsToEmailRaw(subjectRef.current.innerHTML) : (template.subject || "");
    const body = bodyRef.current ? chipsToEmailRaw(bodyRef.current.innerHTML) : (template.body || "");
    await onSave({ ...template, subject: subj, body });
    setSaving(false);
  };

  const autoVars = [
    { token: "{{first_name}}", label: "First Name", type: "auto" },
    { token: "{{name}}", label: "Full Name", type: "auto" },
    { token: "{{email}}", label: "Email", type: "auto" },
  ];

  const fillVars = [
    { token: "[property address]", label: "Property Address", type: "fill" },
    { token: "$[price]", label: "Price", type: "fill" },
    { token: "[date]", label: "Date", type: "fill" },
    { token: "[showing time]", label: "Showing Time", type: "fill" },
    { token: "[listing agent name]", label: "Listing Agent", type: "fill" },
    { token: "[lender name]", label: "Lender Name", type: "fill" },
    { token: "[offer amount]", label: "Offer Amount", type: "fill" },
    { token: "[neighborhood]", label: "Neighborhood", type: "fill" },
  ];

  // Extract custom fields already in template (not in predefined lists)
  const predefinedTokens = new Set([...autoVars.map(v => v.token), ...fillVars.map(v => v.token)]);
  const customFieldsInUse = (() => {
    const text = (template.subject || "") + " " + (template.body || "");
    const found = [];
    const re = /\$?\[([^\]]+)\]/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (!predefinedTokens.has(m[0])) found.push({ raw: m[0], label: m[1] });
    }
    return [...new Map(found.map(f => [f.raw, f])).values()];
  })();

  const editableBase = {
    width: "100%", fontSize: 13, border: `1.5px solid ${t.border}`, borderRadius: 8,
    padding: "10px 14px", background: t.surface, color: t.textPrimary,
    fontFamily: font.body, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 12 : 24, animation: "fadeSlide 0.3s ease" }}>
      {/* Placeholder CSS for empty contentEditable */}
      <style>{`
        [data-chip-editor]:empty::before {
          content: attr(data-placeholder);
          color: ${t.textMuted};
          opacity: 0.5;
          pointer-events: none;
        }
      `}</style>

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
          cursor: "pointer", color: t.textSecondary, fontSize: 13, fontFamily: font.body, padding: 0,
        }}
          onMouseEnter={(e) => (e.currentTarget.style.color = t.emerald)}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textSecondary)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          Back to Templates
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" size="sm" onClick={onBack}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : template.id ? "Save Changes" : "Create Template"}
          </Button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 240px", gap: 20 }}>
        {/* Left — Form with contentEditable fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, display: "block", marginBottom: 6 }}>Template Name</label>
            <input
              value={template.name}
              onChange={(e) => onChange({ ...template, name: e.target.value })}
              placeholder="New Lead Welcome"
              style={editableBase}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, display: "block", marginBottom: 6 }}>Subject</label>
            <div
              ref={subjectRef}
              contentEditable
              suppressContentEditableWarning
              data-chip-editor="1"
              data-placeholder="Welcome aboard!"
              onInput={syncToParent}
              onClick={handleEditorClick}
              onPaste={handlePaste}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              onFocus={() => { lastFocusRef.current = "subject"; }}
              style={{ ...editableBase, minHeight: 20, whiteSpace: "nowrap", overflow: "hidden" }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, display: "block", marginBottom: 6 }}>Body</label>
            {/* Formatting toolbar */}
            <div style={{
              display: "flex", alignItems: "center", gap: 2, padding: "6px 10px",
              background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderBottom: "none",
              borderRadius: "8px 8px 0 0",
            }}>
              {[
                { cmd: "bold", icon: <strong>B</strong>, title: "Bold" },
                { cmd: "italic", icon: <em>I</em>, title: "Italic" },
                { cmd: "underline", icon: <u>U</u>, title: "Underline" },
                { cmd: "strikeThrough", icon: <s>S</s>, title: "Strikethrough" },
              ].map(btn => (
                <button key={btn.cmd} title={btn.title} onMouseDown={(e) => {
                  e.preventDefault();
                  document.execCommand(btn.cmd, false, null);
                  syncToParent();
                }} style={{
                  width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                  background: "none", border: "none", borderRadius: 4, cursor: "pointer",
                  color: t.textSecondary, fontSize: 13, fontFamily: "serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = t.surface}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >{btn.icon}</button>
              ))}
              <div style={{ width: 1, height: 18, background: t.border, margin: "0 4px" }} />
              {[
                { cmd: "insertUnorderedList", icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="3" cy="4" r="1" fill="currentColor" stroke="none"/><circle cx="3" cy="8" r="1" fill="currentColor" stroke="none"/><circle cx="3" cy="12" r="1" fill="currentColor" stroke="none"/><line x1="6" y1="4" x2="14" y2="4"/><line x1="6" y1="8" x2="14" y2="8"/><line x1="6" y1="12" x2="14" y2="12"/></svg>, title: "Bullet List" },
                { cmd: "insertOrderedList", icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><text x="1" y="5.5" fill="currentColor" stroke="none" fontSize="5" fontFamily="sans-serif">1</text><text x="1" y="9.5" fill="currentColor" stroke="none" fontSize="5" fontFamily="sans-serif">2</text><text x="1" y="13.5" fill="currentColor" stroke="none" fontSize="5" fontFamily="sans-serif">3</text><line x1="6" y1="4" x2="14" y2="4"/><line x1="6" y1="8" x2="14" y2="8"/><line x1="6" y1="12" x2="14" y2="12"/></svg>, title: "Numbered List" },
              ].map(btn => (
                <button key={btn.cmd} title={btn.title} onMouseDown={(e) => {
                  e.preventDefault();
                  document.execCommand(btn.cmd, false, null);
                  syncToParent();
                }} style={{
                  width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                  background: "none", border: "none", borderRadius: 4, cursor: "pointer",
                  color: t.textSecondary,
                }}
                  onMouseEnter={e => e.currentTarget.style.background = t.surface}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >{btn.icon}</button>
              ))}
              <div style={{ width: 1, height: 18, background: t.border, margin: "0 4px" }} />
              <button title="Insert Link" onMouseDown={(e) => {
                e.preventDefault();
                const url = prompt("Enter URL:");
                if (url) document.execCommand("createLink", false, url);
                syncToParent();
              }} style={{
                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                background: "none", border: "none", borderRadius: 4, cursor: "pointer",
                color: t.textSecondary,
              }}
                onMouseEnter={e => e.currentTarget.style.background = t.surface}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6.5 9.5a3 3 0 004.24 0l2-2a3 3 0 00-4.24-4.24l-1 1"/><path d="M9.5 6.5a3 3 0 00-4.24 0l-2 2a3 3 0 004.24 4.24l1-1"/></svg>
              </button>
              <button title="Remove Link" onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand("unlink", false, null);
                syncToParent();
              }} style={{
                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                background: "none", border: "none", borderRadius: 4, cursor: "pointer",
                color: t.textMuted, fontSize: 11,
              }}
                onMouseEnter={e => e.currentTarget.style.background = t.surface}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6.5 9.5a3 3 0 004.24 0l2-2a3 3 0 00-4.24-4.24l-1 1"/><path d="M9.5 6.5a3 3 0 00-4.24 0l-2 2a3 3 0 004.24 4.24l1-1"/><line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.5"/></svg>
              </button>
              <div style={{ width: 1, height: 18, background: t.border, margin: "0 4px" }} />
              <select onMouseDown={(e) => e.stopPropagation()} onChange={(e) => {
                if (e.target.value) {
                  document.execCommand("fontSize", false, "7");
                  const fonts = bodyRef.current?.querySelectorAll('font[size="7"]');
                  if (fonts) fonts.forEach(el => {
                    el.removeAttribute("size");
                    el.style.fontSize = e.target.value;
                  });
                  syncToParent();
                }
                e.target.value = "";
              }} style={{
                fontSize: 11, padding: "3px 6px", border: `1px solid ${t.border}`, borderRadius: 4,
                background: t.surface, color: t.textSecondary, cursor: "pointer", fontFamily: font.body,
              }}>
                <option value="">Size</option>
                <option value="12px">Small</option>
                <option value="14px">Normal</option>
                <option value="18px">Large</option>
                <option value="24px">Heading</option>
              </select>
            </div>
            <div
              ref={bodyRef}
              contentEditable
              suppressContentEditableWarning
              data-chip-editor="1"
              data-placeholder="Write your email here..."
              onInput={syncToParent}
              onClick={handleEditorClick}
              onPaste={handlePaste}
              onFocus={() => { lastFocusRef.current = "body"; }}
              style={{ ...editableBase, minHeight: 340, lineHeight: 1.7, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", borderRadius: "0 0 8px 8px" }}
            />
          </div>
        </div>

        {/* Right — Clickable field chips */}
        <div ref={fieldsRef} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Auto-fill */}
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.body }}>Client Fields</div>
            <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 10, fontFamily: font.body }}>Auto-filled from client. Click to insert.</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {autoVars.map(v => (
                <div key={v.token} onClick={() => insertChip(v.type, v.token, v.label)} style={{
                  padding: "4px 10px", borderRadius: 5, cursor: "pointer",
                  background: `${t.emerald}12`, border: `1.5px solid ${t.emerald}`,
                  fontSize: 11, fontWeight: 600, color: t.emerald, fontFamily: font.body,
                  transition: "opacity 0.12s",
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >{v.label}</div>
              ))}
            </div>
          </div>

          {/* Fill-in */}
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.body }}>Fill-in Fields</div>
            <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 10, fontFamily: font.body }}>Agent fills these when sending. Click to insert.</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {fillVars.map(v => (
                <div key={v.token} onClick={() => insertChip(v.type, v.token, v.label)} style={{
                  padding: "4px 10px", borderRadius: 5, cursor: "pointer",
                  background: t.surfaceWarm, border: `1.5px dashed ${t.textMuted}`,
                  fontSize: 11, fontWeight: 600, color: t.textSecondary, fontFamily: font.body,
                  transition: "opacity 0.12s",
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >{v.label}</div>
              ))}
            </div>
          </div>

          {/* Custom Fields */}
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.body }}>Add Custom Field</div>
            <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 10, fontFamily: font.body }}>Create your own placeholder fields</div>

            {/* Type selector pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
              {CUSTOM_FIELD_TYPES.map(ct => (
                <div key={ct.key} onClick={() => setCustomType(ct.key)} style={{
                  padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 600,
                  background: customType === ct.key ? t.navy : t.surfaceWarm,
                  color: customType === ct.key ? "#fff" : t.textMuted,
                  fontFamily: font.body, transition: "all 0.15s",
                }}>{ct.label}</div>
              ))}
            </div>

            {/* Input + Add button */}
            <div style={{ display: "flex", gap: 6 }}>
              <input
                value={customName}
                onChange={e => setCustomName(e.target.value)}
                placeholder="Field name..."
                style={{
                  flex: 1, fontSize: 11, padding: "6px 10px", border: `1px solid ${t.border}`,
                  borderRadius: 6, background: t.pageBg, color: t.textPrimary, outline: "none",
                  fontFamily: font.body,
                }}
                onKeyDown={e => { if (e.key === "Enter" && customName.trim()) handleAddCustomField(); }}
              />
              <button onClick={handleAddCustomField} disabled={!customName.trim()} style={{
                padding: "6px 12px", fontSize: 11, fontWeight: 600, borderRadius: 6, border: "none",
                background: customName.trim() ? t.emerald : t.border,
                color: customName.trim() ? "#fff" : t.textMuted,
                cursor: customName.trim() ? "pointer" : "default",
                fontFamily: font.body, transition: "all 0.15s",
              }}>Add</button>
            </div>

            {/* Existing custom fields in this template */}
            {customFieldsInUse.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 6, fontFamily: font.body }}>In this template:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {customFieldsInUse.map(f => (
                    <div key={f.raw} onClick={() => insertChip("fill", f.raw, f.label)} style={{
                      padding: "4px 10px", borderRadius: 5, cursor: "pointer",
                      background: `${t.sky || "#4A90D9"}12`, border: `1.5px solid ${t.sky || "#4A90D9"}`,
                      fontSize: 11, fontWeight: 600, color: t.sky || "#4A90D9", fontFamily: font.body,
                      transition: "opacity 0.12s",
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                    >{f.label}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Search Bar ============
function SearchBar({ search, onSearch, placeholder, tabs, active, onTab }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 18px", borderBottom: `0.5px solid ${t.border}`, background: t.pageBg, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, minWidth: 120, background: t.surface, border: `0.5px solid ${t.border}`, borderRadius: 6, padding: "6px 10px" }}>
        <Icon size={13} style={{ color: t.textMuted }}><circle cx="6.5" cy="6.5" r="4.5" /><path d="M10 10l3 3" /></Icon>
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder}
          style={{ border: "none", outline: "none", fontSize: 13, background: "transparent", color: t.textPrimary, flex: 1, fontFamily: font.body }} />
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => onTab(tab.key)} style={{
            fontSize: 12, padding: "5px 11px", border: active === tab.key ? "none" : `0.5px solid ${t.border}`,
            borderRadius: 20, cursor: "pointer", whiteSpace: "nowrap", fontFamily: font.body,
            background: active === tab.key ? t.navy : t.surface, color: active === tab.key ? t.goldLight : t.textMuted,
          }}>{tab.label}</button>
        ))}
      </div>
    </div>
  );
}
